/**
 * Compliance gate strategy per customer type (dbo.EntityRuleGateEnforcements).
 * Only gates whose strategy the code acts on are listed (today: FraudPointRisk,
 * read by FraudPointRiskGateService at commit). Allowed strategies come from
 * the gate's own AllowedStrategiesJson; the effect text comes from the server.
 */

import { useMemo, useState } from 'react';

import { saveRiskGate, type RiskGateRow } from '@/services/riskSettingsApi';

import { ConfirmChangesDialog, type PendingChange } from './ConfirmChangesDialog';

const STRATEGY_LABEL: Record<string, string> = {
  'required-hard-stop': 'Block (hard stop)',
  'warn-only': 'Warn only',
  'deferred-N-days-post-acctadd': 'Deferred after opening',
  'deferred-conditional': 'Deferred when conditions met',
  'officer-override': 'Officer override',
};

const label = (s: string): string => STRATEGY_LABEL[s] ?? s;
const rowId = (g: RiskGateRow): string => `${g.gateKey}/${g.customerType}`;

interface Props {
  readonly gates: ReadonlyArray<RiskGateRow>;
  readonly effective: Readonly<Record<string, string>>;
  readonly takesEffect: string;
  readonly onSaved: (message: string) => Promise<void>;
  readonly onError: (message: string) => void;
}

export function GateStrategyCard({ gates, effective, takesEffect, onSaved, onError }: Props): JSX.Element | null {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const dirty = useMemo(() => gates.filter((g) => rowId(g) in draft && draft[rowId(g)] !== g.strategy), [gates, draft]);
  if (gates.length === 0) return null;

  const byGate = new Map<string, RiskGateRow[]>();
  gates.forEach((g) => byGate.set(g.gateKey, [...(byGate.get(g.gateKey) ?? []), g]));

  const pending: PendingChange[] = dirty.map((g) => ({
    id: rowId(g),
    label: `${g.gateName}: ${g.customerType}`,
    oldText: label(g.strategy),
    newText: label(draft[rowId(g)]),
  }));

  const save = async (reason: string): Promise<void> => {
    setBusy(true);
    const done: string[] = [];
    try {
      for (const g of dirty) {
        await saveRiskGate(g.gateKey, g.customerType, draft[rowId(g)], reason);
        done.push(`${g.customerType} → ${label(draft[rowId(g)])}`);
      }
      setDraft({});
      setConfirming(false);
      await onSaved(`Gate saved: ${done.join('; ')}. ${takesEffect}`);
    } catch (e) {
      setConfirming(false);
      setDraft({});
      onError(`${done.length > 0 ? `Saved ${done.join('; ')}, then stopped: ` : 'Nothing was saved: '}${e instanceof Error ? e.message : 'Save failed.'}`);
      await onSaved('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 'var(--sp-9)' }}>
      {[...byGate.entries()].map(([gateKey, rows]) => (
        <div key={gateKey}>
          <h3 style={{ marginBottom: 4 }}>{rows[0].gateName}</h3>
          {rows[0].gateDescription && <p className="rs-group__intro">{rows[0].gateDescription}</p>}
          <table className="table rs-log">
            <thead>
              <tr>
                <th scope="col">Customer type</th>
                <th scope="col">When the follow-up is not done</th>
                <th scope="col">In use now</th>
                <th scope="col">Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => {
                const id = rowId(g);
                const value = id in draft ? draft[id] : g.strategy;
                const effect = g.strategies.find((s) => s.value === value)?.effect;
                return (
                  <tr key={id} className={dirty.includes(g) ? 'rs-row--dirty' : undefined}>
                    <td>{g.customerType}</td>
                    <td style={{ minWidth: 240 }}>
                      <select className="rs-select" value={value} disabled={busy} aria-label={`${g.gateName} for ${g.customerType}`}
                        onChange={(e) => setDraft((d) => ({ ...d, [id]: e.target.value }))}>
                        {g.strategies.map((s) => (
                          <option key={s.value} value={s.value}>
                            {label(s.value)}{s.value === g.defaultStrategy ? ' (procedure default)' : ''}
                          </option>
                        ))}
                      </select>
                      {effect && <div className="rs-option-hint">{effect}</div>}
                    </td>
                    <td>
                      <span className={`pill ${effective[id] === g.strategy ? 'pill--ok' : 'pill--warn'}`}>{label(effective[id] ?? g.strategy)}</span>
                      {g.isOverride && g.updatedBy && (
                        <div className="rs-option-hint">Changed by {g.updatedBy}{g.overrideReason ? `: ${g.overrideReason}` : ''}</div>
                      )}
                    </td>
                    <td className="rs-option-hint">{g.citationLabel ?? g.citationKey}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      <div className="rs-actions">
        <span className="rs-actions__count">
          {dirty.length === 0 ? 'No unsaved gate changes' : `${dirty.length} unsaved gate change${dirty.length === 1 ? '' : 's'}`}
        </span>
        <button type="button" className="btn btn--ghost" disabled={busy || dirty.length === 0} onClick={() => setDraft({})}>Cancel</button>
        <button type="button" className="btn btn--primary" disabled={busy || dirty.length === 0} onClick={() => setConfirming(true)}>
          Save gate changes
        </button>
      </div>

      {confirming && (
        <ConfirmChangesDialog
          title="Change how the gate is enforced?"
          changes={pending}
          takesEffect={takesEffect}
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={(reason) => void save(reason)}
        />
      )}
    </div>
  );
}
